import { memo } from 'react';
import { useTranslation } from 'src/hooks/use-translation';
import Collapse from '@mui/material/Collapse';
import ListSubheader from '@mui/material/ListSubheader';
import Stack from '@mui/material/Stack';

import { useBoolean } from 'src/hooks/use-boolean';
import { onActivationKey } from 'src/utils/keyboard';

import { NavGroupProps, NavProps } from '../types';

import { NavList } from './nav-list';

// ----------------------------------------------------------------------

const NavSection = memo(({ data, slotProps, ...other }: NavProps) => {
  const { t } = useTranslation('', { keyPrefix: 'sidebar' });

  return (
    <Stack component="nav" id="nav-section-vertical" aria-label={t('navigation')} {...other}>
      {data.map((group, index) => (
        <Group
          key={group.subheader || index}
          subheader={group.subheader}
          items={group.items}
          slotProps={slotProps}
        />
      ))}
    </Stack>
  );
});

NavSection.displayName = 'NavSection';

export { NavSection };

// ----------------------------------------------------------------------

function Group({ subheader, items, slotProps }: NavGroupProps) {
  const { value: open, onToggle: handleToggle } = useBoolean(true);

  const renderContent = items.map((list) => (
    <NavList key={list.title} data={list} depth={1} slotProps={slotProps} />
  ));

  return (
    <Stack sx={{ px: 2 }}>
      {subheader ? (
        <>
          <ListSubheader
            disableGutters
            disableSticky
            role="button"
            tabIndex={0}
            aria-expanded={open}
            onClick={handleToggle}
            onKeyDown={onActivationKey(handleToggle)}
            sx={{
              cursor: 'pointer',
              typography: 'overline',
              display: 'inline-flex',
              color: 'text.disabled',
              mb: `${slotProps?.gap || 4}px`,
              p: (theme) => theme.spacing(2, 1, 1, 1.5),
              transition: (theme) =>
                theme.transitions.create(['color'], {
                  duration: theme.transitions.duration.shortest,
                }),
              '&:hover': {
                color: 'text.primary',
              },
              ...slotProps?.subheader,
            }}
          >
            {subheader}
          </ListSubheader>

          <Collapse in={open}>{renderContent}</Collapse>
        </>
      ) : (
        renderContent
      )}
    </Stack>
  );
}
